import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import './pillnav.css';

const PillNav = ({
  logo,
  logoAlt = 'Logo',
  logoHref = '/',
  items = [],
  activeHref,
  className = '',
  ease = 'power3.easeOut',
  baseColor = '#fff',
  pillColor = '#060010',
  hoveredPillTextColor = '#060010',
  pillTextColor,
  activePillColor,
  activePillTextColor,
  onMobileMenuClick,
  theme = 'color',
  initialLoadAnimation = true
}) => {
  const resolvedPillTextColor = pillTextColor ?? baseColor;
  const resolvedActivePillColor = activePillColor ?? pillColor;
  const resolvedActivePillTextColor = activePillTextColor ?? resolvedPillTextColor;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const circleRefs = useRef([]);
  const timelineRefs = useRef([]);
  const activeTweenRefs = useRef([]);
  const logoImgRef = useRef(null);
  const logoTweenRef = useRef(null);
  const hamburgerRef = useRef(null);
  const navItemsRef = useRef(null);
  const logoRef = useRef(null);

  const isExternalLink = (href = '') =>
    href.startsWith('http://') ||
    href.startsWith('https://') ||
    href.startsWith('//') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:') ||
    href.startsWith('#');

  const isRouterLink = (href) => Boolean(href) && !isExternalLink(href);

  const animateMobileMenu = useCallback((nextState) => {
    const hamburger = hamburgerRef.current;
    if (hamburger) {
      const lines = hamburger.querySelectorAll('.hamburger-line');
      if (nextState) {
        gsap.to(lines[0], { rotation: 45, y: 3, duration: 0.3, ease });
        gsap.to(lines[1], { rotation: -45, y: -3, duration: 0.3, ease });
      } else {
        gsap.to(lines[0], { rotation: 0, y: 0, duration: 0.3, ease });
        gsap.to(lines[1], { rotation: 0, y: 0, duration: 0.3, ease });
      }
    }
  }, [ease]);

  const setMobileMenuState = useCallback((nextState) => {
    setIsMobileMenuOpen(nextState);
    animateMobileMenu(nextState);
  }, [animateMobileMenu]);

  useEffect(() => {
    circleRefs.current = circleRefs.current.slice(0, items.length);
    timelineRefs.current = timelineRefs.current.slice(0, items.length);
    activeTweenRefs.current = activeTweenRefs.current.slice(0, items.length);

    const layout = () => {
      circleRefs.current.forEach((circle, index) => {
        if (!circle?.parentElement) {
          return;
        }

        const pill = circle.parentElement;
        const rect = pill.getBoundingClientRect();
        const { width, height } = rect;
        const radius = ((width * width) / 4 + height * height) / (2 * height);
        const diameter = Math.ceil(2 * radius) + 2;
        const delta =
          Math.ceil(radius - Math.sqrt(Math.max(0, radius * radius - (width * width) / 4))) + 1;
        const originY = diameter - delta;

        circle.style.width = `${diameter}px`;
        circle.style.height = `${diameter}px`;
        circle.style.bottom = `-${delta}px`;

        gsap.set(circle, {
          xPercent: -50,
          scale: 0,
          transformOrigin: `50% ${originY}px`
        });

        const label = pill.querySelector('.pill-label');
        const hoverLabel = pill.querySelector('.pill-label-hover');

        if (label) {
          gsap.set(label, { y: 0 });
        }

        if (hoverLabel) {
          gsap.set(hoverLabel, { y: height + 12, opacity: 0 });
        }

        timelineRefs.current[index]?.kill();
        const timeline = gsap.timeline({ paused: true });

        timeline.to(
          circle,
          { scale: 1.2, xPercent: -50, duration: 2, ease, overwrite: 'auto' },
          0
        );

        if (label) {
          timeline.to(label, { y: -(height + 8), duration: 2, ease, overwrite: 'auto' }, 0);
        }

        if (hoverLabel) {
          gsap.set(hoverLabel, { y: Math.ceil(height + 100), opacity: 0 });
          timeline.to(hoverLabel, { y: 0, opacity: 1, duration: 2, ease, overwrite: 'auto' }, 0);
        }

        timelineRefs.current[index] = timeline;
      });
    };

    layout();

    const onResize = () => {
      layout();

      if (window.innerWidth > 768) {
        setIsMobileMenuOpen((open) => {
          if (open) animateMobileMenu(false);
          return false;
        });
      }
    };

    window.addEventListener('resize', onResize);

    if (document.fonts?.ready) {
      document.fonts.ready.then(layout).catch(() => {});
    }


    if (initialLoadAnimation) {
      const logoElement = logoRef.current;
      const navItemsElement = navItemsRef.current;

      if (logoElement) {
        gsap.set(logoElement, { scale: 0 });
        gsap.to(logoElement, {
          scale: 1,
          duration: 0.6,
          ease
        });
      }

      if (navItemsElement) {
        gsap.set(navItemsElement, { width: 0, overflow: 'hidden' });
        gsap.to(navItemsElement, {
          width: 'auto',
          duration: 0.6,
          ease
        });
      }
    }

    return () => {
      window.removeEventListener('resize', onResize);
      timelineRefs.current.forEach((timeline) => timeline?.kill());
      activeTweenRefs.current.forEach((tween) => tween?.kill());
      logoTweenRef.current?.kill();
    };
  }, [ease, initialLoadAnimation, items.length]);

  const handleEnter = (index) => {
    const timeline = timelineRefs.current[index];

    if (!timeline) {
      return;
    }

    activeTweenRefs.current[index]?.kill();
    activeTweenRefs.current[index] = timeline.tweenTo(timeline.duration(), {
      duration: 0.3,
      ease,
      overwrite: 'auto'
    });
  };

  const handleLeave = (index) => {
    const timeline = timelineRefs.current[index];

    if (!timeline) {
      return;
    }

    activeTweenRefs.current[index]?.kill();
    activeTweenRefs.current[index] = timeline.tweenTo(0, {
      duration: 0.2,
      ease,
      overwrite: 'auto'
    });
  };

  const handleLogoEnter = () => {
    const img = logoImgRef.current;

    if (!img) {
      return;
    }

    logoTweenRef.current?.kill();
    gsap.set(img, { rotate: 0 });
    logoTweenRef.current = gsap.to(img, {
      rotate: 360,
      duration: 0.2,
      ease,
      overwrite: 'auto'
    });
  };

  const toggleMobileMenu = () => {
    const nextState = !isMobileMenuOpen;
    setMobileMenuState(nextState);
    onMobileMenuClick?.();
  };

  const handleMobileLinkClick = () => {
    if (isMobileMenuOpen) {
      setMobileMenuState(false);
    }
  };

  const cssVars = {
    '--base': baseColor,
    '--pill-bg': pillColor,
    '--hover-text': hoveredPillTextColor,
    '--pill-text': resolvedPillTextColor,
    '--pill-active-bg': resolvedActivePillColor,
    '--pill-active-text': resolvedActivePillTextColor
  };

  const renderLink = (item, index, mobile = false) => {
    const key = item.href || `item-${index}`;
    const commonClassName = mobile
      ? `mobile-menu-link${activeHref === item.href ? ' is-active' : ''}`
      : `pill${activeHref === item.href ? ' is-active' : ''}`;
    const commonProps = mobile
      ? {
          className: commonClassName,
          onClick: handleMobileLinkClick
        }
      : {
          className: commonClassName,
          'aria-label': item.ariaLabel || item.label,
          onMouseEnter: () => handleEnter(index),
          onMouseLeave: () => handleLeave(index),
          role: 'menuitem'
        };

    if (isRouterLink(item.href)) {
      return mobile ? (
        <li key={key}>
          <Link to={item.href} {...commonProps}>
            {item.label}
          </Link>
        </li>
      ) : (
        <li key={key} role="none">
          <Link to={item.href} {...commonProps}>
            <span
              className="hover-circle"
              aria-hidden="true"
              ref={(element) => {
                circleRefs.current[index] = element;
              }}
            />
            <span className="label-stack">
              <span className="pill-label">{item.label}</span>
              <span className="pill-label-hover" aria-hidden="true">
                {item.label}
              </span>
            </span>
          </Link>
        </li>
      );
    }

    return mobile ? (
      <li key={key}>
        <a href={item.href} {...commonProps}>
          {item.label}
        </a>
      </li>
    ) : (
      <li key={key} role="none">
        <a href={item.href} {...commonProps}>
          <span
            className="hover-circle"
            aria-hidden="true"
            ref={(element) => {
              circleRefs.current[index] = element;
            }}
          />
          <span className="label-stack">
            <span className="pill-label">{item.label}</span>
            <span className="pill-label-hover" aria-hidden="true">
              {item.label}
            </span>
          </span>
        </a>
      </li>
    );
  };

  const logoTarget = logoHref || items[0]?.href || '/';

  return (
    <div className="pill-nav-container">
      <nav
        className={`pill-nav ${className}`.trim()}
        aria-label="Primary"
        data-theme={theme}
        style={cssVars}
      >
        {isRouterLink(logoTarget) ? (
          <Link
            className="pill-logo"
            to={logoTarget}
            aria-label="Home"
            onMouseEnter={handleLogoEnter}
            ref={(element) => {
              logoRef.current = element;
            }}
          >
            <img src={logo} alt={logoAlt} ref={logoImgRef} />
          </Link>
        ) : (
          <a
            className="pill-logo"
            href={logoTarget}
            aria-label="Home"
            onMouseEnter={handleLogoEnter}
            ref={(element) => {
              logoRef.current = element;
            }}
          >
            <img src={logo} alt={logoAlt} ref={logoImgRef} />
          </a>
        )}

        <div className="pill-nav-items desktop-only" ref={navItemsRef}>
          <ul className="pill-list" role="menubar">
            {items.map((item, index) => renderLink(item, index))}
          </ul>
        </div>

        <button
          type="button"
          className="mobile-menu-button mobile-only"
          onClick={toggleMobileMenu}
          aria-expanded={isMobileMenuOpen}
          aria-label="Toggle menu"
          ref={hamburgerRef}
        >
          <span className="hamburger-line" />
          <span className="hamburger-line" />
        </button>
      </nav>

      <div className={`mobile-menu-popover mobile-only${isMobileMenuOpen ? ' is-open' : ''}`} style={cssVars}>
        <ul className="mobile-menu-list">{items.map((item, index) => renderLink(item, index, true))}</ul>
      </div>
    </div>
  );
};

export default PillNav;
